// Edición de vendedor: formulario y body del PUT de `retail-org` con solo lo que cambió.
import { z } from 'zod';
import { EmploymentTypeEnum } from '@fiado/type-kit/bin/retailOrg/enums/EmploymentTypeEnum';
import type { UpdateRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/UpdateRetailUserRequest';
import type { RetailUser } from '@/api/services/retail-org/retail-org.client';
import { optionalMxPhoneField } from '@/lib/formValidators';
import { fromStoredPhone, toPhoneDigits } from '@/lib/phoneMx';

const EMPTY_PHONE_MESSAGE = 'Escribe un teléfono de 10 dígitos.';

/**
 * El back no borra el teléfono: si ya tenía uno, no puede quedar vacío. El formato de 10 dígitos
 * solo se exige si cambió, para que un teléfono viejo no impida guardar lo demás. El nombre solo
 * puede quedar vacío si ya venía vacío.
 */
export const editSellerSchema = ({
    hadPhone,
    originalPhone,
    originalName,
}: {
    hadPhone: boolean;
    originalPhone: string;
    originalName: string;
}) =>
    z.object({
        displayName: z
            .string()
            .trim()
            .refine((value) => value !== '' || originalName === '', 'Escribe el nombre.'),
        phone: z.string().superRefine((value, ctx) => {
            const digits = toPhoneDigits(value);
            if (digits === '') {
                if (hadPhone) ctx.addIssue({ code: 'custom', message: EMPTY_PHONE_MESSAGE });
                return;
            }
            if (value === originalPhone) return;
            const result = optionalMxPhoneField.safeParse(value);
            if (!result.success) {
                ctx.addIssue({ code: 'custom', message: result.error.issues[0]?.message });
            }
        }),
        employmentType: z.nativeEnum(EmploymentTypeEnum),
    });

export type EditSellerValues = z.input<ReturnType<typeof editSellerSchema>>;

/** `fallbackName` prellena el nombre de quien no lo tiene en el padrón. */
export function editFormOf(user: RetailUser, fallbackName = ''): EditSellerValues {
    return {
        displayName: user.displayName ?? fallbackName,
        phone: fromStoredPhone(user.phone),
        employmentType: user.employmentType,
    };
}

/** Solo lo que cambió; `null` si no hay nada que guardar. */
export function toUpdateRequest(
    values: EditSellerValues,
    user: RetailUser,
    fallbackName = '',
): UpdateRetailUserRequest | null {
    const body: UpdateRetailUserRequest = {};
    const name = values.displayName.trim();
    if (name !== (user.displayName ?? fallbackName)) body.displayName = name;
    const phone = toPhoneDigits(values.phone);
    if (phone !== '' && phone !== fromStoredPhone(user.phone)) body.phone = phone;
    if (values.employmentType !== user.employmentType) {
        body.employmentType = values.employmentType;
    }
    return Object.keys(body).length === 0 ? null : body;
}
